import { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Slider,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  Checkroom as CheckroomIcon,
  Palette as PaletteIcon,
} from '@mui/icons-material';
import { FashionService } from '../services/fashionService';
import { 
  FashionSession, 
  FashionSessionStorage, 
  MultiFashionRequest,
  SEASONS,
  TARGET_MARKETS,
  PRICE_POINTS,
  THEME_SUGGESTIONS,
  COLOR_SUGGESTIONS
} from '../models/fashion';
import { useAssetRetrieval } from '../hooks/useAssetRetrieval';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`fashion-tabpanel-${index}`}
      aria-labelledby={`fashion-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
}

// Reusable component for formatted markdown display
interface MarkdownDisplayProps {
  content: string;
}

const MarkdownDisplay: React.FC<MarkdownDisplayProps> = ({ content }: MarkdownDisplayProps) => {
  // Handle empty or whitespace-only content
  const cleanContent = content?.trim() || '';
  
  // Debug logging for technical content
  if (content.includes('Technical') || content.includes('technical')) {
    console.log('MarkdownDisplay received technical content:', content.substring(0, 200));
  }
  
  // Clean up the content for better formatting
  const processedContent = cleanContent
    // Remove markdown formatting artifacts
    .replace(/\*\*(.*?)\*\*/g, '**$1**') // Preserve bold
    .replace(/\*(.*?)\*/g, '*$1*') // Preserve italic
    // Clean up excessive whitespace
    .replace(/\n\s*\n\s*\n+/g, '\n\n') // Reduce multiple newlines
    .replace(/ {3,}/g, '  ') // Reduce excessive spaces but keep intentional indentation
    .replace(/\n +/g, '\n') // Remove leading spaces after newlines (except for code blocks)
    // Fix bullet points
    .replace(/\n\s*\*\s+/g, '\n* ') // Standardize bullet format
    .replace(/\n\s*-\s+/g, '\n- ') // Standardize dash format
    // Ensure proper spacing around headers
    .replace(/(#+\s*[^\n]*)\n([^\n])/g, '$1\n\n$2')
    .trim();
  
  if (!cleanContent) {
    return (
      <Box sx={{ 
        backgroundColor: '#1a1a1a',
        padding: '16px',
        borderRadius: 2,
        textAlign: 'center'
      }}>
        <Typography variant="body2" sx={{ color: '#cccccc', fontStyle: 'italic' }}>
          No content available
        </Typography>
      </Box>
    );
  }
  
  // Check if content looks like it might not be markdown (e.g., plain text response)
  const hasMarkdownElements = processedContent.includes('#') || 
                              processedContent.includes('**') || 
                              processedContent.includes('*') || 
                              processedContent.includes('-') ||
                              processedContent.includes('1.');
                              
  // If it doesn't look like markdown, format it as plain text with better structure
  if (!hasMarkdownElements && processedContent.length > 100) {
    const formattedPlainText = processedContent
      .split('\n\n')
      .map((paragraph: string) => paragraph.trim())
      .filter((paragraph: string) => paragraph.length > 0)
      .join('\n\n');
      
    return (
      <Box sx={{ 
        backgroundColor: '#1a1a1a',
        color: '#ffffff',
        padding: '16px',
        borderRadius: 2,
      }}>
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#ffffff', 
            lineHeight: 1.6, 
            whiteSpace: 'pre-wrap',
            fontSize: '0.95rem'
          }}
        >
          {formattedPlainText}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      backgroundColor: '#1a1a1a',
      color: '#ffffff',
      padding: '16px',
      borderRadius: 2,
      
      // Override any potentially conflicting styles
      '& *': { 
        fontFamily: 'inherit !important' 
      },
      
      // Paragraphs and text
      '& p': { margin: '8px 0', fontSize: '0.95rem', lineHeight: 1.6, color: '#ffffff !important' }, 
      '& p:first-of-type': { marginTop: 0 },
      '& p:last-of-type': { marginBottom: 0 },
      
      // Headers
      '& h1': { margin: '16px 0 12px 0', fontSize: '1.3rem', fontWeight: 700, color: '#ffffff !important' },
      '& h2': { margin: '14px 0 10px 0', fontSize: '1.2rem', fontWeight: 600, color: '#ffffff !important' },
      '& h3': { margin: '12px 0 8px 0', fontSize: '1.1rem', fontWeight: 600, color: '#ffffff !important' },
      '& h4': { margin: '10px 0 6px 0', fontSize: '1.05rem', fontWeight: 600, color: '#ffffff !important' },
      '& h5': { margin: '8px 0 4px 0', fontSize: '1rem', fontWeight: 600, color: '#ffffff !important' },
      '& h6': { margin: '8px 0 4px 0', fontSize: '0.95rem', fontWeight: 600, color: '#cccccc !important' },
      
      // Lists
      '& ul, & ol': { margin: '8px 0', paddingLeft: '24px' },
      '& li': { margin: '4px 0', lineHeight: 1.6, color: '#ffffff !important' },
      '& li > p': { margin: '2px 0', color: '#ffffff !important' },
      
      // Nested lists
      '& ul ul, & ol ol, & ul ol, & ol ul': { margin: '4px 0', paddingLeft: '20px' },
      
      // Blockquotes
      '& blockquote': { 
        borderLeft: '4px solid', 
        borderLeftColor: '#64b5f6', 
        pl: 2, 
        ml: 0, 
        my: 1,
        fontStyle: 'italic',
        backgroundColor: '#333333',
        color: '#e3f2fd !important',
        py: 1
      },
      
      // Code and pre blocks - completely override any pre-existing styles
      '& code': { 
        backgroundColor: '#333333 !important', 
        color: '#81c784 !important',
        padding: '2px 6px', 
        borderRadius: 1, 
        fontFamily: 'Consolas, Monaco, "Courier New", monospace !important',
        fontSize: '0.85rem',
        whiteSpace: 'pre-wrap !important'
      },
      '& pre': { 
        backgroundColor: '#2d2d2d !important', 
        color: '#ffffff !important',
        padding: '12px', 
        borderRadius: 1, 
        overflow: 'auto',
        margin: '8px 0',
        fontSize: '0.85rem',
        lineHeight: 1.4,
        border: '1px solid #444444',
        whiteSpace: 'pre-wrap !important',
        fontFamily: 'Consolas, Monaco, "Courier New", monospace !important'
      },
      '& pre code': { 
        backgroundColor: 'transparent !important', 
        color: '#ffffff !important',
        padding: 0 
      },
      
      // Tables
      '& table': { 
        borderCollapse: 'collapse', 
        width: '100%', 
        margin: '12px 0',
        border: '1px solid #444444'
      },
      '& th, & td': { 
        border: '1px solid #444444',
        padding: '8px 12px', 
        textAlign: 'left',
        color: '#ffffff !important'
      },
      '& th': { 
        backgroundColor: '#333333',
        fontWeight: 600 
      },
      
      // Horizontal rules
      '& hr': { 
        border: 'none',
        borderTop: '1px solid #444444',
        margin: '16px 0'
      },
      
      // Links
      '& a': { 
        color: '#64b5f6 !important',
        textDecoration: 'underline',
        '&:hover': {
          color: '#90caf9 !important',
          textDecoration: 'none'
        }
      },
      
      // Strong and emphasis
      '& strong': { fontWeight: 600, color: '#ffffff !important' },
      '& em': { fontStyle: 'italic', color: '#ffffff !important' }
    }}>
      <ReactMarkdown 
        components={{
          // Force specific rendering for all components
          p: ({children}) => <p style={{color: '#ffffff', margin: '8px 0', fontSize: '0.95rem', lineHeight: 1.6}}>{children}</p>,
          h1: ({children}) => <h1 style={{color: '#ffffff', margin: '16px 0 12px 0', fontSize: '1.3rem', fontWeight: 700}}>{children}</h1>,
          h2: ({children}) => <h2 style={{color: '#ffffff', margin: '14px 0 10px 0', fontSize: '1.2rem', fontWeight: 600}}>{children}</h2>,
          h3: ({children}) => <h3 style={{color: '#ffffff', margin: '12px 0 8px 0', fontSize: '1.1rem', fontWeight: 600}}>{children}</h3>,
          h4: ({children}) => <h4 style={{color: '#ffffff', margin: '10px 0 6px 0', fontSize: '1.05rem', fontWeight: 600}}>{children}</h4>,
          h5: ({children}) => <h5 style={{color: '#ffffff', margin: '8px 0 4px 0', fontSize: '1rem', fontWeight: 600}}>{children}</h5>,
          h6: ({children}) => <h6 style={{color: '#cccccc', margin: '8px 0 4px 0', fontSize: '0.95rem', fontWeight: 600}}>{children}</h6>,
          ul: ({children}) => <ul style={{color: '#ffffff', margin: '8px 0', paddingLeft: '24px'}}>{children}</ul>,
          ol: ({children}) => <ol style={{color: '#ffffff', margin: '8px 0', paddingLeft: '24px'}}>{children}</ol>,
          li: ({children}) => <li style={{color: '#ffffff', margin: '4px 0', lineHeight: 1.6}}>{children}</li>,
          strong: ({children}) => <strong style={{color: '#ffffff', fontWeight: 600}}>{children}</strong>,
          em: ({children}) => <em style={{color: '#ffffff', fontStyle: 'italic'}}>{children}</em>,
          code: ({children}) => <code style={{backgroundColor: '#333333', color: '#81c784', padding: '2px 6px', borderRadius: 4, fontFamily: 'Consolas, Monaco, "Courier New", monospace', fontSize: '0.85rem'}}>{children}</code>,
          pre: ({children}) => <pre style={{backgroundColor: '#2d2d2d', color: '#ffffff', padding: '12px', borderRadius: 4, overflow: 'auto', margin: '8px 0', fontSize: '0.85rem', lineHeight: 1.4, border: '1px solid #444444'}}>{children}</pre>,
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </Box>
  );
};

const FashionMultiAgent: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [tabValue, setTabValue] = useState(0);
  const [designBrief, setDesignBrief] = useState('');
  const [targetMarket, setTargetMarket] = useState('');
  const [season, setSeason] = useState('');
  const [pricePoint, setPricePoint] = useState('');
  const [themeElements, setThemeElements] = useState<string[]>([]);
  const [colorPalette, setColorPalette] = useState<string[]>([]);
  const [fabricPreferences, setFabricPreferences] = useState('');
  const [includeSustainability, setIncludeSustainability] = useState(false);
  const [iterations, setIterations] = useState(3);
  const [maxTokens, setMaxTokens] = useState(2048);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trendAnalysis, setTrendAnalysis] = useState('');
  const [designConcept, setDesignConcept] = useState('');
  const [technicalSpecs, setTechnicalSpecs] = useState('');
  const [sessions, setSessions] = useState<FashionSession[]>([]);

  // Backend asset retrieval
  const {
    assets: backendAssets,
    loading: backendLoading,
    error: backendError,
    refetch: refetchBackendData
  } = useAssetRetrieval({
    agentType: 'FashionDesignMultiAgent',
    sessionBased: false,
    autoFetch: true,
  });

  // Load sessions from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('fashionSessions');
    if (stored) {
      const data: FashionSessionStorage = JSON.parse(stored);
      setSessions(data.sessions.map(s => ({
        ...s,
        createdAt: new Date(s.createdAt)
      })));
    }
  }, []);

  // Merge backend assets with local data
  useEffect(() => {
    const stored = localStorage.getItem('fashionSessions');
    let localData: FashionSession[] = [];
    if (stored) {
      const data: FashionSessionStorage = JSON.parse(stored);
      localData = data.sessions.map(s => ({
        ...s,
        createdAt: new Date(s.createdAt)
      }));
    }

    if (backendAssets && backendAssets.length > 0) {
      console.log('[FashionMultiAgent] Processing backend assets:', backendAssets.length);

      // Group assets by sessionId
      const sessionMap = new Map<string, FashionSession>();

      backendAssets.forEach(asset => {
        // Skip assets without sessionId (legacy assets)
        if (!asset.sessionId) {
          console.warn('[FashionMultiAgent] Skipping asset without sessionId:', asset.id);
          return;
        }

        // Parse metadataJson to get session parameters
        let sessionData = null;
        try {
          if (asset.metadataJson) {
            sessionData = JSON.parse(asset.metadataJson);
          }
        } catch (error) {
          console.warn('[FashionMultiAgent] Failed to parse metadataJson:', error);
        }

        // Get or create session for this sessionId
        let session = sessionMap.get(asset.sessionId);
        if (!session) {
          // Create new session with metadata
          session = {
            id: asset.sessionId,
            designBrief: sessionData?.designBrief || asset.promptUsed || '',
            targetMarket: sessionData?.targetMarket,
            season: sessionData?.season,
            pricePoint: sessionData?.pricePoint,
            themeElements: sessionData?.themeElements,
            colorPalette: sessionData?.colorPalette,
            fabricPreferences: sessionData?.fabricPreferences,
            trendAnalysis: '',
            designConcept: '',
            technicalSpecifications: '',
            createdAt: new Date(asset.createdAt),
            includeSustainability: sessionData?.includeSustainability
          };
          sessionMap.set(asset.sessionId, session);
        }

        // Add content based on subType
        if (asset.subType === 'TrendAnalysis' && asset.textContent) {
          session.trendAnalysis = asset.textContent;
        } else if (asset.subType === 'DesignConcept' && asset.textContent) {
          session.designConcept = asset.textContent;
        } else if (asset.subType === 'TechnicalSpecifications' && asset.textContent) {
          session.technicalSpecifications = asset.textContent;
        }
      });

      const converted = Array.from(sessionMap.values());
      console.log('[FashionMultiAgent] Converted to sessions:', converted.length);

      const backendIds = new Set(converted.map(s => s.id));
      const localOnly = localData.filter(s => !backendIds.has(s.id));
      setSessions([...converted, ...localOnly]);
    }
  }, [backendAssets]);

  // Save sessions to localStorage
  const saveSessions = (newSessions: FashionSession[]) => {
    const storage: FashionSessionStorage = {
      sessions: newSessions,
      lastUpdated: new Date()
    };
    localStorage.setItem('fashionSessions', JSON.stringify(storage));
    setSessions(newSessions);
  };

  const handleGenerate = async () => {
    if (!designBrief.trim()) {
      setError('Please enter a design brief');
      return;
    }

    setLoading(true);
    setError(null);
    setTrendAnalysis('');
    setDesignConcept('');
    setTechnicalSpecs('');

    try {
      const request: MultiFashionRequest = {
        designBrief: designBrief.trim(),
        targetMarket: targetMarket || undefined,
        season: season || undefined,
        pricePoint: pricePoint || undefined,
        themeElements: themeElements.length > 0 ? themeElements : undefined,
        colorPalette: colorPalette.length > 0 ? colorPalette : undefined,
        fabricPreferences: fabricPreferences.trim() || undefined,
        includeSustainability,
        iterations,
        maxTokensPerAgentTurn: maxTokens
      };

      const response = await FashionService.generateFashionDesign(request);
      
      if (response) {
        setTrendAnalysis(response.trendAnalysis || '');
        setDesignConcept(response.designConcept || '');
        setTechnicalSpecs(response.technicalSpecifications || '');
        
        // Save to sessions
        const newSession: FashionSession = {
          id: Date.now().toString(),
          designBrief: designBrief.trim(),
          targetMarket: targetMarket || undefined,
          season: season || undefined,
          pricePoint: pricePoint || undefined,
          themeElements: themeElements.length > 0 ? themeElements : undefined,
          colorPalette: colorPalette.length > 0 ? colorPalette : undefined,
          fabricPreferences: fabricPreferences.trim() || undefined,
          trendAnalysis: response.trendAnalysis,
          designConcept: response.designConcept,
          technicalSpecifications: response.technicalSpecifications,
          createdAt: new Date(),
          includeSustainability
        };
        
        saveSessions([newSession, ...sessions]);
        setTabValue(1); // Switch to results tab
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate fashion design');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleDownload = () => {
    const content = `# Fashion Design Collection

## Design Brief
${designBrief}

${targetMarket ? `**Target Market:** ${targetMarket}\n` : ''}
${season ? `**Season:** ${season}\n` : ''}
${pricePoint ? `**Price Point:** ${pricePoint}\n` : ''}
${themeElements.length > 0 ? `**Theme Elements:** ${themeElements.join(', ')}\n` : ''}
${colorPalette.length > 0 ? `**Color Palette:** ${colorPalette.join(', ')}\n` : ''}
${fabricPreferences ? `**Fabric Preferences:** ${fabricPreferences}\n` : ''}
${includeSustainability ? '**Sustainability:** Included\n' : ''}

## Trend Analysis
${trendAnalysis}

## Design Concept
${designConcept}

## Technical Specifications
${technicalSpecs}
`;

    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fashion-design-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleLoadSession = (session: FashionSession) => {
    setDesignBrief(session.designBrief);
    setTargetMarket(session.targetMarket || '');
    setSeason(session.season || '');
    setPricePoint(session.pricePoint || '');
    setThemeElements(session.themeElements || []);
    setColorPalette(session.colorPalette || []);
    setFabricPreferences(session.fabricPreferences || '');
    setTrendAnalysis(session.trendAnalysis || '');
    setDesignConcept(session.designConcept || '');
    setTechnicalSpecs(session.technicalSpecifications || '');
    setIncludeSustainability(session.includeSustainability ?? false);
    setTabValue(1); // Switch to results tab
  };

  const handleDeleteSession = (sessionId: string) => {
    saveSessions(sessions.filter(s => s.id !== sessionId));
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3, md: 4 } }}>
        <Typography 
          variant="h4" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: { xs: 1, sm: 1.5 }, 
            mb: { xs: 0.5, sm: 1 },
            fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' }
          }}
        >
          <CheckroomIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
          {isMobile ? 'Fashion Agent' : 'Fashion Design Multi-Agent'}
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          {isMobile 
            ? 'Create fashion collections with AI' 
            : 'Create comprehensive fashion collections with AI agents specializing in trends, design, and technical specs'
          }
        </Typography>
      </Box>

      <Paper 
        elevation={0}
        sx={{ 
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 3 } }}>

        {error && (
          <Alert 
            severity="error" 
            onClose={() => setError(null)} 
            sx={{ 
              mb: 2,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            {error}
          </Alert>
        )}

        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ mb: 2 }}
          variant={isMobile ? "fullWidth" : "standard"}
        >
          <Tab 
            label="Generate" 
            sx={{ 
              minHeight: 48,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          />
          <Tab 
            label="Results" 
            sx={{ 
              minHeight: 48,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          />
          <Tab 
            label="History" 
            sx={{ 
              minHeight: 48,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3, md: 4 }, maxWidth: 600, mx: 'auto' }}>
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              label={isMobile ? "" : "Design Brief"}
              placeholder={isMobile ? "Collection concept..." : "Describe your fashion collection concept..."}
              value={designBrief}
              onChange={(e) => setDesignBrief(e.target.value)}
              required
              helperText={!isMobile && "Describe the vision, inspiration, and goals for your fashion collection"}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />

            <Grid container spacing={isMobile ? 1 : 2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {isMobile ? 'Market' : 'Target Market'}
                  </InputLabel>
                  <Select
                    value={targetMarket}
                    onChange={(e) => setTargetMarket(e.target.value)}
                    label={isMobile ? 'Market' : 'Target Market'}
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    <MenuItem value="">None</MenuItem>
                    {TARGET_MARKETS.map((market) => (
                      <MenuItem 
                        key={market} 
                        value={market}
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        {market}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Season</InputLabel>
                  <Select
                    value={season}
                    onChange={(e) => setSeason(e.target.value)}
                    label="Season"
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    <MenuItem value="">None</MenuItem>
                    {SEASONS.map((s) => (
                      <MenuItem 
                        key={s} 
                        value={s}
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {isMobile ? 'Price' : 'Price Point'}
                  </InputLabel>
                  <Select
                    value={pricePoint}
                    onChange={(e) => setPricePoint(e.target.value)}
                    label={isMobile ? 'Price' : 'Price Point'}
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    <MenuItem value="">None</MenuItem>
                    {PRICE_POINTS.map((price) => (
                      <MenuItem 
                        key={price} 
                        value={price}
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        {price}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box>
              <Typography 
                variant="subtitle1" 
                gutterBottom
                sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}
              >
                {isMobile ? 'Themes' : 'Theme Elements'}
              </Typography>
              <Autocomplete
                multiple
                options={THEME_SUGGESTIONS}
                value={themeElements}
                onChange={(_, newValue) => setThemeElements(newValue)}
                freeSolo
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={isMobile ? "Add themes..." : "Select or add theme elements"}
                    InputProps={{
                      ...params.InputProps,
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                  />
                )}
              />
            </Box>

            <Box>
              <Typography 
                variant="subtitle1" 
                gutterBottom 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  fontSize: { xs: '0.95rem', sm: '1rem' }
                }}
              >
                <PaletteIcon sx={{ fontSize: { xs: 20, sm: 24 } }} /> 
                {isMobile ? 'Colors' : 'Color Palette'}
              </Typography>
              <Autocomplete
                multiple
                options={COLOR_SUGGESTIONS}
                value={colorPalette}
                onChange={(_, newValue) => setColorPalette(newValue)}
                freeSolo
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                      sx={{ 
                        backgroundColor: 'rgba(0,0,0,0.05)',
                        fontSize: { xs: '0.7rem', sm: '0.75rem' }
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={isMobile ? "Add colors..." : "Select or add colors"}
                    InputProps={{
                      ...params.InputProps,
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                  />
                )}
              />
            </Box>

            <TextField
              fullWidth
              multiline
              rows={2}
              label={isMobile ? "" : "Fabric Preferences"}
              placeholder={isMobile ? "Fabric preferences..." : "e.g., Organic cotton, silk blends, technical fabrics..."}
              value={fabricPreferences}
              onChange={(e) => setFabricPreferences(e.target.value)}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={includeSustainability}
                  onChange={(e) => setIncludeSustainability(e.target.checked)}
                />
              }
              label={
                <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {isMobile ? 'Sustainability' : 'Include sustainability considerations'}
                </Typography>
              }
            />

            <Box>
              <Typography 
                gutterBottom
                sx={{ 
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', sm: '0.95rem' },
                  mb: 2
                }}
              >
                {isMobile ? 'Iterations' : 'Agent Iterations'}: {iterations}
              </Typography>
              <Slider
                value={iterations}
                onChange={(_, value) => setIterations(value as number)}
                min={1}
                max={5}
                marks
                valueLabelDisplay="auto"
              />
            </Box>

            <Box>
              <Typography 
                gutterBottom
                sx={{ 
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', sm: '0.95rem' },
                  mb: 2
                }}
              >
                {isMobile ? 'Tokens' : 'Max Tokens per Agent'}: {maxTokens}
              </Typography>
              <Slider
                value={maxTokens}
                onChange={(_, value) => setMaxTokens(value as number)}
                min={512}
                max={4096}
                step={256}
                valueLabelDisplay="auto"
              />
            </Box>

            <Button
              variant="contained"
              size={isMobile ? "medium" : "large"}
              onClick={handleGenerate}
              disabled={loading || !designBrief.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <CheckroomIcon />)}
              sx={{ 
                alignSelf: 'flex-start',
                py: 1.5,
                px: 4,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: { xs: '0.875rem', sm: '0.95rem', md: '1rem' },
                mt: 2,
                minHeight: 44
              }}
            >
              {loading ? (isMobile ? 'Creating...' : 'Creating Collection...') : (isMobile ? 'Generate' : 'Generate Fashion Collection')}
            </Button>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {(trendAnalysis || designConcept || technicalSpecs) ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: { xs: 1, sm: 2 } }}>
                <Button
                  startIcon={!isMobile && <DownloadIcon />}
                  onClick={handleDownload}
                  variant="outlined"
                  size={isMobile ? "small" : "medium"}
                  sx={{ 
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    minHeight: 44
                  }}
                >
                  {isMobile ? 'Download' : 'Download Complete Collection'}
                </Button>
              </Box>

              {trendAnalysis && (
                <Card variant="outlined">
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1.5, sm: 2 } }}>
                      <Typography 
                        variant="h6"
                        sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                      >
                        {isMobile ? 'Trend Analysis' : 'Trend Analysis'}
                      </Typography>
                      <Tooltip title={isMobile ? "" : "Copy"}>
                        <IconButton 
                          onClick={() => handleCopy(trendAnalysis)} 
                          size="small"
                          sx={{ minWidth: 44, minHeight: 44 }}
                        >
                          <CopyIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <MarkdownDisplay key={`trend-${trendAnalysis.length}`} content={trendAnalysis} />
                  </CardContent>
                </Card>
              )}

              {designConcept && (
                <Card variant="outlined">
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1.5, sm: 2 } }}>
                      <Typography 
                        variant="h6"
                        sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                      >
                        {isMobile ? 'Design' : 'Design Concept'}
                      </Typography>
                      <Tooltip title={isMobile ? "" : "Copy"}>
                        <IconButton 
                          onClick={() => handleCopy(designConcept)} 
                          size="small"
                          sx={{ minWidth: 44, minHeight: 44 }}
                        >
                          <CopyIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <MarkdownDisplay key={`concept-${designConcept.length}`} content={designConcept} />
                  </CardContent>
                </Card>
              )}

              {technicalSpecs && (
                <Card variant="outlined">
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1.5, sm: 2 } }}>
                      <Typography 
                        variant="h6"
                        sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                      >
                        {isMobile ? 'Tech Specs' : 'Technical Specifications'}
                      </Typography>
                      <Tooltip title={isMobile ? "" : "Copy"}>
                        <IconButton 
                          onClick={() => handleCopy(technicalSpecs)} 
                          size="small"
                          sx={{ minWidth: 44, minHeight: 44 }}
                        >
                          <CopyIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <MarkdownDisplay key={`technical-${technicalSpecs.length}`} content={technicalSpecs} />
                  </CardContent>
                </Card>
              )}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 6, md: 8 } }}>
              <CheckroomIcon sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: { xs: 1, sm: 2 } }} />
              <Typography 
                variant="h6" 
                color="text.secondary"
                sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
              >
                {isMobile ? 'No collection yet' : 'No fashion collection generated yet'}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mt: 1,
                  fontSize: { xs: '0.875rem', sm: '0.95rem' }
                }}
              >
                {isMobile ? 'Go to Generate to create one' : 'Go to the Design Brief tab to create your collection'}
              </Typography>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {sessions.length > 0 ? (
            <List>
              {sessions.map((session) => (
                <ListItem 
                  key={session.id} 
                  divider
                  sx={{ 
                    cursor: 'pointer',
                    py: { xs: 1.5, sm: 2 }
                  }}
                  onClick={() => handleLoadSession(session)}
                >
                  <ListItemText
                    primary={session.designBrief.substring(0, isMobile ? 50 : 100) + '...'}
                    secondary={
                      isMobile ? (
                        `Created: ${session.createdAt.toLocaleDateString()}`
                      ) : (
                        <>
                          {session.targetMarket && `${session.targetMarket} • `}
                          {session.season && `${session.season} • `}
                          {session.pricePoint && `${session.pricePoint} • `}
                          Created: {session.createdAt.toLocaleDateString()}
                        </>
                      )
                    }
                    primaryTypographyProps={{
                      sx: { fontSize: { xs: '0.95rem', sm: '1rem' } }
                    }}
                    secondaryTypographyProps={{
                      sx: { fontSize: { xs: '0.8rem', sm: '0.875rem' } }
                    }}
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title={isMobile ? "" : "Delete"}>
                      <IconButton 
                        edge="end" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSession(session.id);
                        }}
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 6, md: 8 } }}>
              <HistoryIcon sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: { xs: 1, sm: 2 } }} />
              <Typography 
                variant="h6" 
                color="text.secondary"
                sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
              >
                {isMobile ? 'No history' : 'No fashion collections in history'}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mt: 1,
                  fontSize: { xs: '0.875rem', sm: '0.95rem' }
                }}
              >
                {isMobile ? 'Collections will appear here' : 'Generated collections will appear here'}
              </Typography>
            </Box>
          )}
        </TabPanel>
        </Box>
      </Paper>
    </Container>
  );
};

export default FashionMultiAgent;